// components/market/ThemeProvider.tsx
//
// Fournit le ui_config d'une boutique (couleurs, typographie, layout)
// via Context à tous les composants enfants de la page boutique, pour
// que ProductCard/ShopHeader/etc. se stylent dynamiquement sans avoir
// à recevoir ui_config en prop à chaque niveau.
//
// Gère aussi theme_mode === 'auto' via useColorScheme() de React Native
// (suit le réglage clair/sombre de l'appareil du visiteur) : expose un
// flag isDark dérivé, que les composants enfants utilisent pour adapter
// leur propre contraste si besoin (le jeu de couleurs choisi par le
// vendeur dans ui_config.colors reste la source de vérité — isDark sert
// seulement à savoir si on est actuellement en contexte sombre ou clair
// quand theme_mode est 'auto').

import React, { createContext, useContext, useMemo } from 'react';
import { useColorScheme } from 'react-native';
import type { ShopUiConfig } from '@/lib/market/uiConfigDefaults';
import { DEFAULT_SHOP_UI_CONFIG } from '@/lib/market/uiConfigDefaults';

type MarketThemeContextValue = {
  config: ShopUiConfig;
  // true si le contexte effectif est sombre : soit theme_mode='dark'
  // explicitement, soit theme_mode='auto' ET l'appareil est en mode
  // sombre. false sinon (theme_mode='light', ou 'auto' + appareil clair).
  isDark: boolean;
};

const MarketThemeContext = createContext<MarketThemeContextValue>({
  config: DEFAULT_SHOP_UI_CONFIG,
  isDark: false,
});

export const useMarketTheme = () => useContext(MarketThemeContext);

type MarketThemeProviderProps = {
  config?: ShopUiConfig | null;
  children: React.ReactNode;
};

export default function MarketThemeProvider({ config, children }: MarketThemeProviderProps) {
  const systemScheme = useColorScheme(); // 'light' | 'dark' | null

  const resolvedConfig = config ?? DEFAULT_SHOP_UI_CONFIG;

  const isDark = useMemo(() => {
    if (resolvedConfig.theme_mode === 'dark') return true;
    if (resolvedConfig.theme_mode === 'light') return false;
    // 'auto' : suit le réglage système de l'appareil du visiteur
    return systemScheme === 'dark';
  }, [resolvedConfig.theme_mode, systemScheme]);

  const value = useMemo(
    () => ({ config: resolvedConfig, isDark }),
    [resolvedConfig, isDark]
  );

  return (
    <MarketThemeContext.Provider value={value}>
      {children}
    </MarketThemeContext.Provider>
  );
}
